// ============================================
// document-mention-node-view.tsx — R14/R16 inline mention chip
// ============================================
//
// The React NodeView that renders an `@`-mention as a chip inside the
// TipTap editor.
//
// R16 enhancements:
//   - **Two kinds of chips** — task chips show the status badge
//     (R14 behaviour); person chips show an Avatar + name.
//   - **Person click** — clicking a person chip fires
//     `editor.storage.documentPersonMentionClick(id, label, anchor)`,
//     which the parent (R25, Bug B) wires to a popover listing the
//     tasks assigned to that person (the clicked chip is passed as the
//     popover anchor). Task chips keep the R14 behaviour of opening the
//     task detail panel.
//
// Split out of `document-editor.tsx` so the extension factory
// (`document-mention-extension.ts`) can import it without dragging
// in the Yjs / TipTap wiring.

import { NodeViewWrapper } from '@tiptap/react';
import type { ReactNodeViewProps } from '@tiptap/react';
import { AtSign, User } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { cn } from '@/lib/utils';
import type { ProjectMember, Task, TaskStatus } from '@/lib/schemas';
import { useDocumentI18n } from './document-i18n';

// Use the generic ReactNodeViewProps so we don't have to redeclare
// every TipTap ProseMirror node field. We only need `node.attrs.id`
// + `node.attrs.label` for the chip — `kind` is optional for
// backward-compat with R14 nodes that didn't carry it.
type DocumentMentionChipProps = ReactNodeViewProps<{
    id: string;
    label: string;
    kind?: 'task' | 'person';
}>;

// Lightweight colour map for the chip's status badge — mirrors
// `document-mention-list.tsx` so the picker preview matches the
// rendered chip.
const MENTION_STATUS_BADGE: Record<TaskStatus, string> = {
    TODO: 'bg-slate-500/20 text-slate-300',
    IN_PROGRESS: 'bg-blue-500/20 text-blue-300',
    IN_REVIEW: 'bg-amber-500/20 text-amber-300',
    DONE: 'bg-emerald-500/20 text-emerald-300',
    CANCELLED: 'bg-zinc-500/20 text-zinc-400',
};

const MENTION_STATUS_LABEL: Record<TaskStatus, string> = {
    TODO: 'To do',
    IN_PROGRESS: 'In progress',
    IN_REVIEW: 'In review',
    DONE: 'Done',
    CANCELLED: 'Cancelled',
};

// Module-scope lookup so the NodeView (which lives outside React's
// tree) can subscribe to mention-task changes without forcing a
// TipTap re-render. DocumentView populates this on every
// mentionTasks update via `updateDocumentMentionTasks`.
export const DocumentMentionStatusRegistry: { current: Record<string, Task> } = { current: {} };

/**
 * Mirror registry for project members — used by the person chip to
 * render an Avatar with the right image / fallback initials. Populated
 * by DocumentView on every `mentionMembers` change.
 */
export const DocumentMentionMembersRegistry: { current: Record<string, ProjectMember> } = {
    current: {},
};

/**
 * Push the latest task list into the NodeView lookup table. Called
 * by DocumentEditor on every `mentionTasks` change so the chip's
 * status badge stays in sync with the task list refresh.
 */
export function updateDocumentMentionTasks(tasks: Task[] | undefined): void {
    const next: Record<string, Task> = {};
    if (tasks) {
        for (const t of tasks) next[t.id] = t;
    }
    DocumentMentionStatusRegistry.current = next;
}

/**
 * Push the latest project members list into the NodeView lookup
 * table. Called by DocumentEditor on every `mentionMembers` change
 * so the person chip's Avatar stays in sync with member updates.
 */
export function updateDocumentMentionMembers(
    members: ProjectMember[] | undefined
): void {
    const next: Record<string, ProjectMember> = {};
    if (members) {
        for (const m of members) next[m.user.id] = m;
    }
    DocumentMentionMembersRegistry.current = next;
}

function initialsFor(label: string, email?: string | null): string {
    const source = label || email || '?';
    return source
        .split(/\s+|@/)
        .filter(Boolean)
        .slice(0, 2)
        .map((p) => p[0]?.toUpperCase() ?? '')
        .join('');
}

export function DocumentMentionChipInner(props: DocumentMentionChipProps) {
    const { node, editor } = props;
    const taskId: string = node.attrs.id;
    const label: string = node.attrs.label;
    // R14 nodes predate the `kind` field; default to 'task' so
    // legacy chips keep rendering as task chips.
    const kind: 'task' | 'person' = (node.attrs.kind as 'task' | 'person' | undefined) ?? 'task';
    const { t } = useDocumentI18n();

    const task = DocumentMentionStatusRegistry.current[taskId];
    const member = DocumentMentionMembersRegistry.current[taskId];

    const handleClick: React.MouseEventHandler<HTMLSpanElement> = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (kind === 'person') {
            const handler = (editor.storage as
                | {
                      documentPersonMentionClick?: (
                          id: string,
                          label: string,
                          // R25 (Bug B): the clicked chip, used by the
                          // parent as the popover anchor.
                          anchor?: HTMLElement | null
                      ) => void;
                  }
                | undefined)?.documentPersonMentionClick;
            // Capture the element NOW — `currentTarget` is only valid
            // for the duration of the event dispatch.
            const anchor =
                (e.currentTarget as HTMLElement | null) ?? null;
            handler?.(taskId, label, anchor);
            return;
        }
        // task chip — keep R14 behaviour (open task detail panel)
        const handler = (editor.storage as
            | { documentMentionClick?: (id: string) => void }
            | undefined)?.documentMentionClick;
        handler?.(taskId);
    };

    if (kind === 'person') {
        return (
            <NodeViewWrapper
                as="span"
                className={cn(
                    'document-mention-chip',
                    'document-mention-chip--person',
                    'inline-flex items-center gap-1 rounded-md border border-emerald-500/40 bg-emerald-500/10',
                    'px-1.5 py-0.5 align-baseline text-sm font-medium text-emerald-300',
                    'cursor-pointer transition-colors hover:bg-emerald-500/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500/40'
                )}
                contentEditable={false}
                data-testid="document-mention-node"
                data-person-id={taskId}
                data-mention-kind="person"
                role="button"
                tabIndex={0}
                onClick={handleClick}
                onKeyDown={(e: React.KeyboardEvent<HTMLSpanElement>) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleClick(e as unknown as React.MouseEvent<HTMLSpanElement>);
                    }
                }}
            >
                <User className="h-3 w-3 shrink-0" aria-hidden="true" />
                <Avatar className="h-4 w-4 shrink-0">
                    {member?.user.avatarUrl ? (
                        <AvatarImage src={member.user.avatarUrl} alt={label} />
                    ) : null}
                    <AvatarFallback className="text-[8px] font-semibold">
                        {initialsFor(label, member?.user.email ?? null)}
                    </AvatarFallback>
                </Avatar>
                <span className="truncate" data-testid="document-mention-label">
                    {label}
                </span>
                <span className="text-[10px] font-semibold uppercase tracking-wider opacity-70">
                    <AtSign className="h-2.5 w-2.5" aria-hidden="true" />
                </span>
            </NodeViewWrapper>
        );
    }

    // task chip (R14 default + R16 explicit 'task' kind)
    return (
        <NodeViewWrapper
            as="span"
            className={cn(
                'document-mention-chip',
                'document-mention-chip--task',
                'inline-flex items-center gap-1 rounded-md border border-primary/40 bg-primary/10',
                'px-1.5 py-0.5 align-baseline text-sm font-medium text-primary',
                'cursor-pointer transition-colors hover:bg-primary/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40'
            )}
            contentEditable={false}
            data-testid="document-mention-node"
            data-task-id={taskId}
            data-mention-kind="task"
            role="button"
            tabIndex={0}
            onClick={handleClick}
            onKeyDown={(e: React.KeyboardEvent<HTMLSpanElement>) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleClick(e as unknown as React.MouseEvent<HTMLSpanElement>);
                }
            }}
        >
            <span className="text-[10px] font-semibold uppercase tracking-wider text-primary/80">
                {t('document.mention.task')}
            </span>
            <span className="truncate" data-testid="document-mention-label">
                {label}
            </span>
            {task ? (
                <span
                    className={cn(
                        'ml-0.5 rounded-full px-1 py-px text-[9px] font-medium uppercase tracking-wide',
                        MENTION_STATUS_BADGE[task.status]
                    )}
                >
                    {MENTION_STATUS_LABEL[task.status]}
                </span>
            ) : null}
        </NodeViewWrapper>
    );
}
